import { useState, useEffect } from 'react'
import { lumi } from '../lib/lumi'

export interface SocialLinks {
  twitter?: string
  website?: string
  baseApp?: string
  chart?: string
  farcaster?: string
  facebook?: string
  instagram?: string
  tiktok?: string
  email?: string
  telegram?: string
  nftMarketplace?: string
}

export interface MediaItem {
  url: string
  type: 'image' | 'video'
  caption?: string
  uploadedAt: string
}

export interface Project {
  _id: string
  projectName: string
  description?: string
  websiteUrl?: string
  bannerImageUrl?: string
  profileImageUrl?: string
  mediaGallery?: MediaItem[]
  socialLinks?: SocialLinks
  blockchain?: 'Base' | 'Ethereum' | 'Solana'
  tokenContractAddress?: string
  nftContractAddress?: string
  ownerId: string
  ownerEmail: string
  subscriptionStatus: 'active' | 'inactive' | 'cancelled' | 'pending'
  stripeCustomerId?: string
  stripeSubscriptionId?: string
  clickCount: number
  viewCount: number
  isActive: boolean
  isFeatured?: boolean
  featuredUntil?: string
  featuredPurchases?: number
  lastFeaturedAt?: string
  slug?: string
  isDemo?: boolean
  createdAt: string
  updatedAt: string
}

export function useProjects() {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(false)
  const [lastFetch, setLastFetch] = useState<number>(0)
  const CACHE_DURATION = 30000 // 30 seconds cache

  const fetchProjects = async (filter?: any, forceRefresh = false) => {
    // Implement cache to prevent excessive requests
    const now = Date.now()
    if (!forceRefresh && now - lastFetch < CACHE_DURATION && projects.length > 0 && !filter) {
      console.log('Using cached projects')
      return projects
    }

    setLoading(true)
    try {
      const { list } = await lumi.entities.projects.list({
        filter: filter || { isActive: true, subscriptionStatus: 'active' },
        sort: { createdAt: -1 }
      })
      setProjects(list)
      setLastFetch(now)
      console.log('Projects fetched:', list)
      return list
    } catch (error) {
      console.error('Fetch projects failed:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const fetchUserProjects = async (userId: string) => {
    return fetchProjects({ ownerId: userId })
  }

  const createProject = async (projectData: Partial<Project>) => {
    try {
      // Check for duplicate project name
      const { list: existingProjects } = await lumi.entities.projects.list({
        filter: { projectName: projectData.projectName }
      })
      
      if (existingProjects.length > 0) {
        throw new Error('A project with this name already exists. Please choose a different name.')
      }

      const newProject = await lumi.entities.projects.create({
        ...projectData,
        clickCount: 0,
        viewCount: 0,
        isActive: false,
        subscriptionStatus: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      console.log('Project created:', newProject)
      await fetchProjects()
      return newProject
    } catch (error) {
      console.error('Create project failed:', error)
      throw error
    }
  }

  const updateProject = async (projectId: string, updates: Partial<Project>) => {
    try {
      if (typeof projectId !== 'string') {
        throw new Error('Project ID must be a string')
      }

      // Check for duplicate project name if updating name
      if (updates.projectName) {
        const { list: existingProjects } = await lumi.entities.projects.list({
          filter: { projectName: updates.projectName }
        })
        
        // Ensure the only match is the current project
        const duplicates = existingProjects.filter(p => p._id !== projectId)
        if (duplicates.length > 0) {
          throw new Error('A project with this name already exists. Please choose a different name.')
        }
      }

      const updated = await lumi.entities.projects.update(projectId, {
        ...updates,
        updatedAt: new Date().toISOString()
      })
      console.log('Project updated:', updated)
      await fetchProjects()
      return updated
    } catch (error) {
      console.error('Update project failed:', error)
      throw error
    }
  }

  const deleteProject = async (projectId: string) => {
    try {
      if (typeof projectId !== 'string' || projectId === '[object Object]') {
        throw new Error('Invalid ID: must be a string, not an object')
      }

      await lumi.entities.projects.delete(projectId)
      console.log('Project deleted:', projectId)
      await fetchProjects()
    } catch (error) {
      console.error('Delete project failed:', error)
      throw error
    }
  }

  const incrementClickCount = async (projectId: string) => {
    try {
      const project = projects.find(p => p._id === projectId)
      if (project) {
        await updateProject(projectId, {
          clickCount: project.clickCount + 1
        })
      }
    } catch (error) {
      console.error('Increment click count failed:', error)
    }
  }

  const fetchActiveProjects = async (forceRefresh = false) => {
    return fetchProjects({ isActive: true, subscriptionStatus: 'active' }, forceRefresh)
  }

  const refreshProjects = async () => {
    return fetchProjects(undefined, true)
  }

  return {
    projects,
    loading,
    fetchProjects,
    fetchActiveProjects,
    fetchUserProjects,
    createProject,
    updateProject,
    deleteProject,
    incrementClick: incrementClickCount,
    refreshProjects
  }
}
